import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Platform,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Dimensions,
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from "react-native";
import variables from "../../styles/variables";
import Icon from "../Icon";
import useDeviceType from "../../hooks/useDeviceType";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { RootStackParamList } from "../../types/route";
import { logEvent } from "../../client/analytics/trackPageView";

export type BackToTopProps = {
  scrollViewRef: React.RefObject<ScrollView>;
  tabBarAdBottomAmount?: Animated.Value;
};

export type BackToTopRef = {
  handleScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  getBottomPosition: () => number;
};

// Enable on specific screens (empty => all)
const enableFeatureOn: (keyof RootStackParamList)[] = [
  "IndividualRacecard",
  "IndividualResult",
];

// Add analytics event mapping for each screen if needed in the future
const eventMapping: Partial<Record<keyof RootStackParamList, string>> = {
  IndividualRacecard: "racecard-insights-tab-back-to-top",
  IndividualResult: "results-comparison-back-to-top",
};

const isWeb = Platform.OS === "web";
const buttonSize = 48;
const desktopGapFromEdge = 56;
const mobileGapFromEdge = 16;

const BackToTop = forwardRef<BackToTopRef, BackToTopProps>(
  ({ scrollViewRef, tabBarAdBottomAmount }, ref) => {
    const deviceType = useDeviceType();
    const [isVisible, setIsVisible] = useState(false);

    const fadeAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(0)).current;

    const [windowDimensions, setWindowDimensions] = useState(
      Dimensions.get("window")
    );

    useEffect(() => {
      const onChange = ({ window }) => {
        setWindowDimensions(window);
      };
      const subscription = Dimensions.addEventListener("change", onChange);
      return () => subscription?.remove();
    }, []);

    const { height: screenHeight, width: screenWidth } = windowDimensions;

    const isDesktop = deviceType.deviceType === "DESKTOP";
    const isPhone = deviceType.deviceType === "PHONE";

    // Static gap from the edge as a plain number
    const position = isDesktop ? desktopGapFromEdge : mobileGapFromEdge;

    // We move the button UP by the height of the bottom bar using translateY = -tabBarAdBottomAmount
    const translateY =
      isPhone && isWeb && tabBarAdBottomAmount
        ? Animated.multiply(tabBarAdBottomAmount, -1)
        : 0;

    const leftPosition = screenWidth - position - buttonSize;

    const [isHovered, setIsHovered] = useState(false);
    const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
    const navigationState = navigation.getState();
    const currentRouteName =
      navigationState?.routes?.[navigationState.index]?.name;

    const shouldShowButton =
      !!currentRouteName && enableFeatureOn.includes(currentRouteName);

    const showButton = () => {
      setIsVisible(true);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 100,
          friction: 8,
          useNativeDriver: true,
        }),
      ]).start();
    };

    const hideButton = () => {
      setIsVisible(false);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    };

    const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollY = event.nativeEvent.contentOffset.y;
      const shouldShow = scrollY > screenHeight;

      if (shouldShow && !isVisible) showButton();
      else if (!shouldShow && isVisible) hideButton();
    };

    useImperativeHandle(ref, () => ({
      handleScroll,
      getBottomPosition: () => position, // numeric snapshot of the static base gap
    }));

    // Web scroll listener
    useEffect(() => {
      if (!isWeb) return;
      const handleWebScroll = () => {
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const shouldShow = scrollY > screenHeight;
        if (shouldShow && !isVisible) showButton();
        else if (!shouldShow && isVisible) hideButton();
      };
      window.addEventListener("scroll", handleWebScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleWebScroll);
    }, [screenHeight, isVisible]);

    const scrollToTop = () => {
      if (currentRouteName in eventMapping) {
        logEvent({
          key: eventMapping[currentRouteName],
          data: {},
        });
      }

      if (scrollViewRef?.current) {
        scrollViewRef.current.scrollTo({ y: 0, animated: true });
      } else if (isWeb) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    if (!shouldShowButton) return null;

    return (
      <Animated.View
        style={[
          styles.buttonContainer,
          {
            // layout
            position: isWeb ? "fixed" : "absolute",
            left: leftPosition,
            bottom: position,

            // animation
            opacity: fadeAnim,
            transform: [{ translateY }, { scale: scaleAnim }],
          },
        ]}
        pointerEvents={isVisible ? "auto" : "none"}
      >
        <TouchableOpacity
          accessibilityLabel="back-to-top-button"
          onPress={scrollToTop}
          style={[
            styles.buttonRound,
            isHovered && {
              backgroundColor: variables.colors.palette.rtv.secondaryHover,
            },
          ]}
          activeOpacity={0.8}
          // @ts-ignore (mouse events on web/desktop only)
          onMouseEnter={() => isDesktop && setIsHovered(true)}
          onMouseLeave={() => isDesktop && setIsHovered(false)}
        >
          <Icon name="arrowUpward" size="medium" />
        </TouchableOpacity>
      </Animated.View>
    );
  }
);

export default BackToTop;

const styles = StyleSheet.create({
  buttonContainer: {
    zIndex: 1000,
    height: buttonSize,
    width: buttonSize,
  },
  buttonRound: {
    borderRadius: 40,
    backgroundColor: variables.colors.palette.rtv.secondary,
    height: "100%",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
});
